import { useCallback } from 'react';

import type { PermissibleRouteKeys } from '@/_pages/settings/user-roles/shared';
import { permissionsRouteTree } from '@/_shared';
import type { RouteListItem } from '@/types';

import type { PermissionsType } from './use-auth-values';
import { FINANCIAL_OVERRIDE_PERMISSION_KEY } from './use-financial-override-permission';

export function useLocalPermissions(permissions: PermissionsType) {
  const getPermissibleTabs = useCallback(
    (parent: PermissibleRouteKeys, subNav: RouteListItem[]) => {
      if (!permissions || Object.keys(permissions).length === 0) {
        return subNav.map((item) => ({ ...item, show: false }));
      }

      const { tab } = permissions;
      const hasFinancialOverride = Boolean(
        (tab ?? []).find((t) => String(t.resource) === FINANCIAL_OVERRIDE_PERMISSION_KEY)?.view
      );
      return subNav.map((item) => {
        const { path } = item;
        const pathParts = path.split('/');
        const isTab = pathParts.length > 3;
        if (!isTab) {
          // handle first page
          return { ...item, show: true };
        }
        const matchedPermission = (tab ?? []).find((t) => t.resource === parent + '/' + pathParts[3]);
        if (!matchedPermission) {
          return { ...item, show: false };
        }

        // Special-case gate: Settings > Revenue Lockdowns should only be visible for Financial Override users.
        if (parent === 'settings' && pathParts[3] === 'revenue-lockdowns') {
          return { ...item, show: matchedPermission.view && hasFinancialOverride };
        }

        return { ...item, show: matchedPermission.view };
      }, []);
    },
    [permissions]
  );

  const getModelPermissionByPath = useCallback(
    (rawPath: string, byPassEmptyPermissions = false) => {
      let path = rawPath;
      if (rawPath.endsWith('/')) {
        path = rawPath.slice(0, -1);
      }

      if (path === '/dashboard') {
        return {
          actions: ['view'],
          resource: 'dashboard',
          resource_type: 'model',
          view: true,
          create: false,
          update: false,
          delete: false,
          restore: false,
        };
      }

      if ((!permissions || Object.keys(permissions).length === 0) && !byPassEmptyPermissions) {
        return null;
      }

      // Convert Map values to array and find the route by path
      const route = permissionsRouteTree.find((r) => r.path === path);

      if (!route) {
        console.warn(`Route not found for path: ${path}`);
        return null;
      }
      const { model } = permissions || {};
      const matchedPermission = model?.find((t) => t.resource === route.auditKey);

      if (!matchedPermission) {
        // console.log(`Permission not found for resource: ${path} / ${route.key}`);
        return null;
      }

      if (path === '/settings' && matchedPermission) {
        return {
          ...matchedPermission,
          create: matchedPermission.view,
          update: matchedPermission.view,
          delete: matchedPermission.view,
          restore: matchedPermission.view,
        };
      }

      return matchedPermission;
    },
    [permissions]
  );

  const getPermissionModelDetailsByPath = useCallback((rawPath: string) => {
    // Remove only numeric path segments (e.g., IDs), not all digits globally
    let sanitizedPath = rawPath;
    if (rawPath.endsWith('/')) {
      sanitizedPath = rawPath.slice(0, -1);
    }

    const actualSanitizedPath = sanitizedPath;

    // Parent path is exception for inventory
    if (sanitizedPath.startsWith('/inventory/parent/')) {
      sanitizedPath = sanitizedPath.replace('/inventory/parent/', '/inventory/');
    }

    // keep both separate
    if (sanitizedPath.startsWith('/inventory/parent-new')) {
      sanitizedPath = sanitizedPath.replace('/inventory/parent-new', '/inventory/new');
    }

    // Parent path is exception for inventory
    if (sanitizedPath.startsWith('/invoices/invoice-batch/')) {
      sanitizedPath = sanitizedPath.replace('/invoices/invoice-batch/', '/invoices/');
    }

    sanitizedPath = sanitizedPath
      .split('/')
      .filter((segment) => !/^\d+$/.test(segment))
      .join('/');

    const pathParts = sanitizedPath.split('/');
    let modelPath = '/' + pathParts[1];
    const isModel = pathParts.length < 3;

    if (modelPath === '/pending-deposits') {
      modelPath = '/deposits';
    }

    return {
      pathParts,
      isModel,
      sanitizedPath: actualSanitizedPath,
      modelPath,
    };
  }, []);

  return {
    getPermissibleTabs,
    getModelPermissionByPath,
    getPermissionModelDetailsByPath,
  };
}
